import { ValueAxis } from "@/components/tool-kit/charts/axes";
import { ChartSvg, ChartTitles } from "@/components/tool-kit/charts/chart-svg";
import type { ChartStyle } from "@/components/tool-kit/charts/controls";
import { px } from "@/components/tool-kit/charts/marks";
import { colorIndex, paletteColor } from "@/lib/tool-kit/charts/color";
import {
  type DotAxis,
  type DotStack,
  smallestGap,
} from "@/lib/tool-kit/charts/dot-plot";
import { type NumberFormat, tickFormat } from "@/lib/tool-kit/charts/format";
import { formatFraction } from "@/lib/tool-kit/charts/fractions";
import {
  CHART_FONT_SIZE,
  CHART_WIDTH,
  cartesianFrame,
  heightForPlot,
} from "@/lib/tool-kit/charts/layout";
import { linearScale } from "@/lib/tool-kit/charts/scale";
import { CHART_INK } from "@/lib/tool-kit/palette";

/*
 * A dot plot (a "line plot" in school statistics): a number line with one mark stacked
 * above it per observation. There is no y-axis — the reader counts the marks — so the
 * image grows taller with the tallest stack rather than squashing the dots.
 */

export type DotMark = "dot" | "x";

const MAX_DOT = 20;
const MIN_DOT = 5;
const MAX_STACK_HEIGHT = 320;

function Mark({
  mark,
  x,
  y,
  size,
  color,
}: {
  mark: DotMark;
  x: number;
  y: number;
  size: number;
  color: string;
}) {
  const half = size / 2 - 1;
  if (mark === "x") {
    const arm = half * 0.8;
    return (
      <g
        stroke={color}
        strokeLinecap="round"
        strokeWidth={Math.max(1.5, size / 7)}
      >
        <line
          x1={px(x - arm)}
          x2={px(x + arm)}
          y1={px(y - arm)}
          y2={px(y + arm)}
        />
        <line
          x1={px(x - arm)}
          x2={px(x + arm)}
          y1={px(y + arm)}
          y2={px(y - arm)}
        />
      </g>
    );
  }
  return <circle cx={px(x)} cy={px(y)} fill={color} r={px(half)} />;
}

export function DotPlotChart({
  id,
  stacks,
  axis,
  mark,
  style,
  fmt,
  label,
  description,
}: {
  /** The `<svg>` id, unique on the page. */
  id: string;
  stacks: DotStack[];
  axis: DotAxis;
  mark: DotMark;
  style: ChartStyle;
  fmt: NumberFormat;
  label: string;
  description: string;
}) {
  const decimal = tickFormat(fmt, axis.decimals);
  const tick = (value: number) =>
    (axis.denominator ? formatFraction(value, axis.denominator) : null) ??
    decimal(value);
  const tallest = Math.max(1, ...stacks.map((stack) => stack.count));
  const input = {
    width: CHART_WIDTH,
    title: style.apa ? "" : style.title,
    xTitle: style.xTitle,
    leftLabelWidth: 0,
    bottomLabelHeight: CHART_FONT_SIZE.tick,
    topPadding: style.valueLabels ? 20 : 10,
  };
  const probe = cartesianFrame({ ...input, height: 400 });
  const span = Math.max(axis.max - axis.min, 1e-9);
  const unit = probe.plot.width / span;
  const gap = Math.min(smallestGap(stacks) ?? axis.step, axis.step);
  let size = Math.max(MIN_DOT, Math.min(MAX_DOT, gap * unit * 0.85));
  size = Math.max(MIN_DOT, Math.min(size, MAX_STACK_HEIGHT / tallest));
  const frame = cartesianFrame({
    ...input,
    height: heightForPlot(Math.max(60, tallest * size + 8), input),
  });
  const { plot } = frame;
  const x = linearScale([axis.min, axis.max], [plot.x, plot.x + plot.width]);
  const base = plot.y + plot.height - 3;
  const color = paletteColor(colorIndex(style.color), 0);
  return (
    <ChartSvg
      description={description}
      height={frame.height}
      id={id}
      label={label}
      width={frame.width}
    >
      <ChartTitles frame={frame} />
      <ValueAxis
        apa={style.apa}
        format={tick}
        grid={false}
        orient="bottom"
        plot={plot}
        scale={x}
        ticks={axis.ticks}
      />
      {stacks.map((stack) => (
        <g key={stack.position}>
          {Array.from({ length: stack.count }, (_, level) => (
            <Mark
              color={color}
              // biome-ignore lint/suspicious/noArrayIndexKey: dots in a stack are identical, bottom up
              key={level}
              mark={mark}
              size={size}
              x={x(stack.position)}
              y={base - size / 2 - level * size}
            />
          ))}
          {style.valueLabels ? (
            <text
              fill={CHART_INK.text}
              fontSize={CHART_FONT_SIZE.value}
              textAnchor="middle"
              x={px(x(stack.position))}
              y={px(base - stack.count * size - 5)}
            >
              {fmt(stack.count)}
            </text>
          ) : null}
        </g>
      ))}
    </ChartSvg>
  );
}
